import { useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Tap-to-record, tap-to-stop mic helper for the AI assistant widgets.
 * Records short audio via MediaRecorder, uploads it to a Whisper
 * transcription endpoint, and hands the resulting text to onResult.
 */
export function useVoiceRecorder(client, path, onResult) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder && MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("file", blob, "voice.webm");
          // Let axios/browser set Content-Type with the multipart boundary
          // automatically — an explicit header here would omit the
          // boundary and break parsing.
          const { data } = await client.post(path, form);
          if (!data?.text) toast.error("Didn't catch that — try again a bit closer to the mic.");
          onResult(data?.text || "");
        } catch (e) {
          toast.error("Couldn't transcribe that recording.");
          onResult("");
        } finally {
          setTranscribing(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access is blocked — allow it in your browser settings to use voice ask.");
      setRecording(false);
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  const toggle = () => (recording ? stop() : start());

  return { recording, transcribing, toggle };
}
